import { useLingui } from '@lingui/react/macro'
import { CantileverButton } from 'components/CantileverButton'
import { Button } from 'designSystem/components/Button'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { LucideIcon, Pencil, Ruler, Type } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const ActiveToolButton = styled(CantileverButton)`
  background: var(--accent);
`

const AnnotationDot = styled.span`
  align-self: center;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: var(--destructive);
`

export type MarkupToolMode = 'label' | 'ruler' | 'point'

interface MarkupToolDef {
  mode: MarkupToolMode
  name: string
  Icon: LucideIcon
}

const MARKUP_TOOLS: Record<MarkupToolMode, MarkupToolDef> = {
  label: { mode: 'label', name: 'Label', Icon: Type },
  ruler: { mode: 'ruler', name: 'Ruler', Icon: Ruler },
  point: { mode: 'point', name: 'Point Annotation', Icon: Pencil },
}

/* The HUD keeps the embed ordering; the workbench toolbar leads with the
   passive tools and ends on Label next to its panel controls. */
const HUD_ORDER: MarkupToolMode[] = ['label', 'ruler', 'point']
const TOOLBAR_ORDER: MarkupToolMode[] = ['ruler', 'point', 'label']

interface MarkupToolButtonsProps {
  appearance?: 'hud' | 'toolbar'
  /** The point tool's availability is chart-state the caller owns. */
  pointDisabled: boolean
  /** Doubles as the idle tooltip: the reason when disabled, else the name. */
  pointDisabledReason: string
  tools?: MarkupToolMode[]
}

export const MarkupToolButtons: React.FC<MarkupToolButtonsProps> = ({
  appearance = 'hud',
  pointDisabled,
  pointDisabledReason,
  tools,
}) => {
  const { t } = useLingui()

  const { interactionMode, setInteractionMode, hasUnsavedAnnotationChanges } =
    useScatterplotContext()

  const order = tools ?? (appearance === 'toolbar' ? TOOLBAR_ORDER : HUD_ORDER)
  const toolProps = (tool: MarkupToolDef) => {
    const active = interactionMode === tool.mode
    const disabled = tool.mode === 'point' && pointDisabled
    const idleLabel = tool.mode === 'point' ? pointDisabledReason : tool.name
    return { active, disabled, idleLabel }
  }

  if (appearance === 'toolbar') {
    return (
      <>
        {order.map((mode) => {
          const tool = MARKUP_TOOLS[mode]
          const { active, disabled, idleLabel } = toolProps(tool)
          return (
            <ChartTooltip
              key={mode}
              label={
                active ? t`${{ value78_35: tool.name }} (active)` : idleLabel
              }
              side='bottom'>
              <Button
                variant='inputRaised'
                size='icon-lg'
                pressed='invert'
                aria-pressed={active}
                aria-label={tool.name}
                disabled={disabled}
                onClick={() => setInteractionMode(active ? 'none' : mode)}>
                <tool.Icon size={18} strokeWidth={1.5} />
              </Button>
            </ChartTooltip>
          )
        })}
        {hasUnsavedAnnotationChanges && (
          <ChartTooltip label={t`Unsaved annotation changes`} side='bottom'>
            <AnnotationDot
              role='status'
              aria-label={t`Unsaved annotation changes`}
            />
          </ChartTooltip>
        )}
      </>
    )
  }

  return (
    <>
      {order.map((mode) => {
        const tool = MARKUP_TOOLS[mode]
        const { active, disabled, idleLabel } = toolProps(tool)
        const ToolButton = active ? ActiveToolButton : CantileverButton
        return (
          <ChartTooltip
            key={mode}
            label={
              active
                ? t`${{ value116_23: tool.name }} (active — click to deactivate, Esc to exit)`
                : idleLabel
            }
            side='top'
            style={{ display: 'inline-flex' }}>
            <ToolButton
              iconOnly
              ghost
              size='small'
              aria-label={tool.name}
              disabled={disabled}
              onClick={() => setInteractionMode(active ? 'none' : mode)}>
              <tool.Icon size={16} />
            </ToolButton>
          </ChartTooltip>
        )
      })}
    </>
  )
}
